/**
 * 👤 Player aliases (v6.43, Javi's brother's idea). What a player calls
 * themselves on the boards, instead of the Google account's real name.
 * Shared by the browser (live hints while typing) and the server (the
 * only writer), so the two can never disagree about what is allowed.
 *
 * Validation is three layers, all on a NORMALISED key so look-alikes
 * cannot slip through: accents stripped, case folded, leet digits mapped
 * back to letters, separators removed, repeated letters collapsed.
 *   1. shape - 3 to 16 characters, letters/digits/space/. _ -, no
 *      leading or trailing space, no double spaces;
 *   2. reserved - names that would impersonate the game or a fallback;
 *   3. blocked - slurs and profanity in en/es/fr. Long, distinctive
 *      terms match anywhere; short ones only as whole words, so
 *      Scunthorpe, bassist and cumbre stay allowed.
 * The list is deliberately clear-cut. Perfect filtering is not the
 * point: the report button and the force-rename lever are the other
 * half (server/api/report, server/api/admin/force-rename).
 */
export const ALIAS_MIN = 3
export const ALIAS_MAX = 16
export const ALIAS_RENAME_COOLDOWN_MS = 7 * 86_400_000
export const ALIAS_FORCED_LOCK_MS = 30 * 86_400_000

export type AliasProblem = 'short' | 'long' | 'chars' | 'reserved' | 'blocked'

const SHAPE = /^[\p{L}\p{N}][\p{L}\p{N} ._-]*$/u

/** Whole-word matches only (short, or common inside innocent words). */
const BLOCKED_WORDS = new Set([
  // en
  'ass', 'anal', 'anus', 'cock', 'cunt', 'dick', 'dyke', 'fag', 'fags', 'gay', 'homo', 'jew', 'jews',
  'kike', 'chink', 'coon', 'gook', 'spic', 'nig', 'nigs', 'paki', 'rape', 'rapist', 'nazi', 'tit', 'tits',
  'twat', 'whore', 'slut', 'cum', 'jizz', 'pedo', 'penis', 'vagina', 'porn', 'sex',
  // es
  'puta', 'puto', 'putas', 'putos', 'culo', 'polla', 'cono', 'zorra', 'pendejo', 'pendeja', 'cabron',
  'cabrona', 'verga', 'chocha', 'pinche', 'mamon', 'marica', 'sudaca', 'gitano', 'moro',
  // fr
  'merde', 'pute', 'bite', 'couille', 'couilles', 'nique', 'niquer', 'pd', 'fdp', 'ntm', 'negre', 'bougnoul',
  'bougnoule', 'youpin', 'chintok', 'salaud', 'connard', 'connasse',
])

/** Distinctive enough to match as a substring of the collapsed key. */
const BLOCKED_PARTS = [
  // en
  'nigger', 'nigga', 'faggot', 'retard', 'fuck', 'shit', 'bitch', 'pussy', 'wetback', 'tranny', 'hitler',
  'motherf', 'cocksuck', 'asshole', 'dickhead', 'blowjob', 'handjob', 'kunt',
  // es
  'joder', 'mierda', 'gilipollas', 'hijodeputa', 'hijaputa', 'maricon', 'maricones', 'putamadre', 'chingar',
  'chinga', 'cabronazo', 'malparido', 'mamaguevo', 'coger', 'pajero', 'negrata', 'panchito',
  // fr
  'encule', 'salope', 'putain', 'enfoire', 'tarlouze', 'batard', 'connards', 'negro', 'bamboula',
]

/** Would impersonate the game, staff, or the automatic fallback names. */
const RESERVED = new Set([
  'admin', 'administrator', 'administrador', 'administrateur', 'mod', 'moderator', 'moderador', 'moderateur',
  'staff', 'support', 'official', 'oficial', 'officiel', 'system', 'systeme', 'sistema', 'dev', 'developer',
  'elemental', 'dispatch', 'elementaldispatch', 'anonymous', 'anonimo', 'anonyme', 'null', 'undefined',
])

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', '$': 's', '!': 'i' }

/** Trim and collapse inner whitespace; what gets stored and shown. */
export function normalizeAlias(raw: string): string {
  return String(raw ?? '').replace(/\s+/g, ' ').trim()
}

/** Case-, accent- and separator-insensitive, digits as typed: what the
 * reserved-name check and the fallback pattern are matched against. */
function plainKey(alias: string): string {
  return normalizeAlias(alias)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, '')
}

/** Case-, accent- and leet-insensitive key: the uniqueness key and the
 * string the blocklist is matched against. Letters of any script stay. */
export function aliasKey(alias: string): string {
  return normalizeAlias(alias)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .replace(/[0134578@$!]/g, ch => LEET[ch] ?? ch)
    .replace(/[^\p{L}\p{N}]+/gu, '')
}

function collapse(key: string): string {
  return key.replace(/(.)\1+/g, '$1')
}

function tokens(alias: string): string[] {
  return normalizeAlias(alias)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .replace(/[0134578@$!]/g, ch => LEET[ch] ?? ch)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

export function aliasProblem(raw: string): AliasProblem | null {
  const alias = normalizeAlias(raw)
  const length = Array.from(alias).length
  if (length < ALIAS_MIN) return 'short'
  if (length > ALIAS_MAX) return 'long'
  if (!SHAPE.test(alias) || alias.includes('  ')) return 'chars'
  const key = aliasKey(alias)
  if (key.length < 2) return 'chars' // punctuation and spaces only
  const plain = plainKey(alias)
  if (RESERVED.has(key) || RESERVED.has(plain) || /^player\d*$/.test(plain)) return 'reserved'
  const short = collapse(key)
  for (const part of BLOCKED_PARTS) {
    if (key.includes(part) || short.includes(part)) return 'blocked'
  }
  for (const tok of tokens(alias)) {
    if (BLOCKED_WORDS.has(tok) || BLOCKED_WORDS.has(collapse(tok))) return 'blocked'
  }
  return null
}

/** What a player who never picked a name is called on the boards. */
export function fallbackAlias(uid: string): string {
  let h = 0
  for (const ch of uid) h = (h * 31 + ch.charCodeAt(0)) % 10_000
  return `Player-${String(h).padStart(4, '0')}`
}
